/**
 * `useComposerPrefillStore` — one-shot text hand-off to the chat
 * composer (ChatInput). Any in-app surface (the source-assessment
 * pane's "Run verify" CTA, future affordances on missions / drafter
 * etc.) writes a directive here; ChatInput consumes it on mount AND
 * on subsequent writes (subscribed), seeds its local `text` state,
 * then clears the slot. Single-slot — successive writes overwrite.
 *
 * Why a store, not a callback: ChatInput is mounted by the case
 * layout; the pane is mounted inside the viewer; both share the
 * route but no common parent close enough to thread props through
 * cleanly. The store is the cheapest decoupled seam.
 */

import { create } from "zustand";

interface ComposerPrefillState {
  /** Text to drop into the chat composer on next consumer tick.
   *  `null` when nothing is pending (consumed or never set). */
  pendingText: string | null;
  /** Producer side — any UI surface calls this with the directive
   *  it wants the concierge to act on. Pre-existing pending text
   *  is overwritten (the user's most recent click wins). */
  setPendingText: (text: string) => void;
  /** Consumer side — ChatInput calls this on mount + on the next
   *  subscribed change, applies the value, and clears the slot so
   *  the prefill doesn't replay on the next mount. Returns the
   *  consumed value (or `null` when nothing was pending). */
  consume: () => string | null;
}

export const useComposerPrefillStore = create<ComposerPrefillState>((set, get) => ({
  pendingText: null,
  setPendingText: (text: string) => set({ pendingText: text }),
  consume: () => {
    const text = get().pendingText;
    if (text !== null) set({ pendingText: null });
    return text;
  },
}));
